import React, { useState, createContext, useMemo, useCallback, useContext, useEffect } from 'react';

// Create the context
export const LoadingContext = createContext({
  isLoading: false,
  // Expose raw count functions for interceptors or manual control
  incrementRequestCount: () => {}, 
  decrementRequestCount: () => {}, 
});

// Hook to easily use the context loading state
export const useLoading = () => {
  const { isLoading } = useContext(LoadingContext);
  return isLoading;
};

// Store context functions globally for interceptor access
// WARNING: This is a simplified approach for demonstration. In larger apps, 
// consider dependency injection or passing the context functions explicitly 
// to your API layer setup.
let globalIncrementRequestCount = () => { console.warn("LoadingContext increment not yet initialized"); };
let globalDecrementRequestCount = () => { console.warn("LoadingContext decrement not yet initialized"); };

// Export functions for interceptor to use
export const triggerShowLoader = () => globalIncrementRequestCount();
export const triggerHideLoader = () => globalDecrementRequestCount();


// Create the provider component
export const LoadingProvider = ({ children }) => {
  const [requestCount, setRequestCount] = useState(0);

  const incrementRequestCount = useCallback(() => {
    console.log("Incrementing loader count");
    // Use functional update to ensure atomicity if called rapidly
    setRequestCount(prevCount => prevCount + 1); 
  }, []);

  const decrementRequestCount = useCallback(() => {
    // Use shorter timeout to prevent rapid flickering but be more responsive
    setTimeout(() => {
      console.log("Decrementing loader count");
       // Use functional update and ensure count doesn't go below 0
      setRequestCount(prevCount => Math.max(0, prevCount - 1));
    }, 50); // Reduced to 50ms for maximum responsiveness
  }, []);

  // Add safeguard: if loading state is stuck for too long, force reset
  useEffect(() => {
    let timeoutId;
    if (requestCount > 0) {
      // If loading for more than 30 seconds, force reset
      timeoutId = setTimeout(() => {
        console.warn("[LoadingContext] Loading state stuck for 30+ seconds, force resetting...");
        setRequestCount(0);
      }, 30000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [requestCount]);

  // Update global functions when context initializes/updates
  // This ensures the interceptors call the latest functions bound to the provider state
  useMemo(() => {
      globalIncrementRequestCount = incrementRequestCount;
      globalDecrementRequestCount = decrementRequestCount;
  }, [incrementRequestCount, decrementRequestCount]);


  // isLoading is true if count > 0
  const isLoading = requestCount > 0;

  // Debug logging for stuck states
  useEffect(() => {
    if (requestCount > 0) {
      console.log(`[LoadingContext] Current request count: ${requestCount}`);
    }
  }, [requestCount]);

  // Memoize the context value that gets passed down
  const value = useMemo(() => ({ 
      isLoading, 
      incrementRequestCount, // Provide these if direct manual control is ever needed
      decrementRequestCount 
  }), [isLoading, incrementRequestCount, decrementRequestCount]);

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
}; 